import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import API from "../Api";
import { useAuth } from "../context/AuthContext";
import "./Login.css";

const FINALIZE_ATTEMPTS = 6;
const FINALIZE_DELAY_MS = 2000;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function hasPaidPlan(plan) {
  if (!plan) return false;
  const normalized = String(plan).toLowerCase();
  return ["starter", "team", "growth", "enterprise"].includes(normalized);
}

async function refreshBillingState(refreshSubscription, refreshUsage) {
  await Promise.all([refreshSubscription(), refreshUsage()]);
}

function BillingSuccess() {
  const navigate = useNavigate();
  const { isAuthenticated, refreshSubscription, refreshUsage } = useAuth();
  const refreshSubscriptionRef = useRef(refreshSubscription);
  const refreshUsageRef = useRef(refreshUsage);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("Finalizing your subscription...");

  refreshSubscriptionRef.current = refreshSubscription;
  refreshUsageRef.current = refreshUsage;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!isAuthenticated) {
        if (!cancelled) {
          setMessage(
            "Subscription checkout finished. If payment succeeded, you can log in with your account."
          );
          setLoading(false);
        }
        return;
      }

      try {
        for (let attempt = 0; attempt < FINALIZE_ATTEMPTS; attempt += 1) {
          if (cancelled) return;

          const sessionId = new URLSearchParams(window.location.search).get("session_id");
          try {
            await API.post("/api/billing/refresh", sessionId ? { sessionId } : {}, { timeout: 20000 });
          } catch (error) {
            // The local billing read below still shows the last confirmed plan.
          }
          await refreshBillingState(
            refreshSubscriptionRef.current,
            refreshUsageRef.current
          );

          let plan = null;
          try {
            const meRes = await API.get("/auth/me");
            plan = meRes?.data?.plan || null;
          } catch (error) {
            // no-op: billing refresh above is still useful
          }

          if (hasPaidPlan(plan) || attempt === FINALIZE_ATTEMPTS - 1) {
            if (!cancelled) {
              setMessage(
                hasPaidPlan(plan)
                  ? "Subscription updated successfully."
                  : "Checkout finished. Your plan may take a moment to appear — try logging in again shortly."
              );
            }
            return;
          }

          await sleep(FINALIZE_DELAY_MS);
        }
      } catch (error) {
        if (!cancelled) {
          setMessage("Checkout finished, but subscription refresh is pending.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  return (
    <div className="login-container">
      <div className="login-box">
        <h2>Billing Success</h2>
        <p className="subtitle">{message}</p>
        {loading && <div className="loading-spinner" style={{ margin: "12px auto" }} />}
        <div className="signup-link" style={{ marginTop: "12px" }}>
          {isAuthenticated ? (
            <>
              <button className="login-button" onClick={() => navigate("/dashboards")}>
                Go to Dashboards
              </button>
              <p style={{ marginTop: "12px" }}>
                Need details? <Link to="/settings/profile">Open Settings</Link>
              </p>
            </>
          ) : (
            <button className="login-button" onClick={() => navigate("/login")}>
              Go to Login
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default BillingSuccess;
